import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Users, DollarSign, Search } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const AccountsReceivable = ({ user }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [paymentData, setPaymentData] = useState({
    monto: '',
    tipo_pago: 'efectivo',
    referencia: '',
    notas: '',
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts-receivable`);
      setAccounts(response.data);
    } catch (error) {
      toast.error('Error al cargar cuentas');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/accounts-receivable/${selectedAccount.id}/payments`, {
        ...paymentData,
        monto: parseFloat(paymentData.monto),
      });
      toast.success('Pago registrado exitosamente');
      setPaymentDialogOpen(false);
      setSelectedAccount(null);
      resetPaymentForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    }
  };

  const resetPaymentForm = () => {
    setPaymentData({
      monto: '',
      tipo_pago: 'efectivo',
      referencia: '',
      notas: '',
    });
  };

  const openPaymentDialog = (account) => {
    setSelectedAccount(account);
    setPaymentData({ ...paymentData, monto: account.monto_pendiente.toString() });
    setPaymentDialogOpen(true);
  };

  const filteredAccounts = accounts.filter(
    (acc) =>
      acc.cliente_nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.factura_numero.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'pagada':
        return 'bg-green-100 text-green-700';
      case 'pendiente':
        return 'bg-amber-100 text-amber-700';
      case 'vencida':
        return 'bg-red-100 text-red-700';
      case 'parcial':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pagada': return 'Pagada';
      case 'pendiente': return 'Pendiente';
      case 'vencida': return 'Vencida';
      case 'parcial': return 'Parcial';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounts-receivable-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Cuentas por Cobrar</h1>
          <p className="text-slate-600 mt-1">Gestiona los créditos a clientes</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Total por Cobrar</span>
            <Users className="text-blue-500" size={20} />
          </div>
          <div className="text-2xl font-bold text-slate-800">
            {formatCurrency(accounts.reduce((sum, acc) => sum + acc.monto_pendiente, 0))}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Cuentas Pendientes</span>
            <Users className="text-amber-500" size={20} />
          </div>
          <div className="text-2xl font-bold text-slate-800">
            {accounts.filter(a => a.status === 'pendiente' || a.status === 'parcial').length}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Cuentas Vencidas</span>
            <Users className="text-red-500" size={20} />
          </div>
          <div className="text-2xl font-bold text-slate-800">
            {accounts.filter(a => a.status === 'vencida').length}
          </div>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
        <Input
          placeholder="Buscar cuentas..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-accounts-input"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="custom-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Factura</th>
                <th>RNC</th>
                <th>Teléfono</th>
                <th>Total</th>
                <th>Pendiente</th>
                <th>Vencimiento</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody data-testid="accounts-table-body">
              {filteredAccounts.map((account) => (
                <tr key={account.id} data-testid={`account-row-${account.id}`}>
                  <td className="font-medium">{account.cliente_nombre}</td>
                  <td>{account.factura_numero}</td>
                  <td className="text-sm">{account.cliente_rnc || '-'}</td>
                  <td className="text-sm">{account.cliente_telefono || '-'}</td>
                  <td className="font-semibold">{formatCurrency(account.monto_total)}</td>
                  <td className="font-semibold text-red-600">{formatCurrency(account.monto_pendiente)}</td>
                  <td className="text-sm">{new Date(account.fecha_vencimiento).toLocaleDateString('es-DO')}</td>
                  <td>
                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(account.status)}`}>
                      {getStatusLabel(account.status)}
                    </span>
                  </td>
                  <td>
                    {account.status !== 'pagada' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPaymentDialog(account)}
                        data-testid={`pay-btn-${account.id}`}
                      >
                        <DollarSign size={16} className="mr-1" />
                        Cobrar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredAccounts.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No se encontraron cuentas por cobrar
          </div>
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent data-testid="payment-dialog">
          {selectedAccount && (
            <>
              <DialogHeader>
                <DialogTitle>Registrar Cobro</DialogTitle>
                <DialogDescription>
                  {selectedAccount.cliente_nombre} - Factura {selectedAccount.factura_numero}
                </DialogDescription>
              </DialogHeader>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span>Total:</span>
                    <span className="font-semibold">{formatCurrency(selectedAccount.monto_total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Pendiente:</span>
                    <span className="font-semibold text-red-600">{formatCurrency(selectedAccount.monto_pendiente)}</span>
                  </div>
                </div>
              </div>
              <form onSubmit={handlePayment} className="space-y-4">
                <div>
                  <Label htmlFor="monto">Monto a Cobrar *</Label>
                  <Input
                    id="monto"
                    type="number"
                    step="0.01"
                    value={paymentData.monto}
                    onChange={(e) => setPaymentData({ ...paymentData, monto: e.target.value })}
                    required
                    max={selectedAccount.monto_pendiente}
                    data-testid="payment-monto-input"
                  />
                </div>
                <div>
                  <Label htmlFor="tipo_pago">Forma de Pago *</Label>
                  <Select value={paymentData.tipo_pago} onValueChange={(value) => setPaymentData({ ...paymentData, tipo_pago: value })}>
                    <SelectTrigger data-testid="payment-tipo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="tarjeta">Tarjeta</SelectItem>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="referencia">Referencia</Label>
                  <Input
                    id="referencia"
                    value={paymentData.referencia}
                    onChange={(e) => setPaymentData({ ...paymentData, referencia: e.target.value })}
                    placeholder="Número de referencia"
                    data-testid="payment-ref-input"
                  />
                </div>
                <div>
                  <Label htmlFor="notas_pago">Notas</Label>
                  <Textarea
                    id="notas_pago"
                    value={paymentData.notas}
                    onChange={(e) => setPaymentData({ ...paymentData, notas: e.target.value })}
                    rows={2}
                    data-testid="payment-notas-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600" data-testid="submit-payment-btn">
                  Registrar Cobro
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsReceivable;
